import { useUser } from "@clerk/nextjs";
import { useQuery } from "@tanstack/react-query";
import { getMatchingLibrariesByEmail } from "helpers/api";
import { useMemo } from "react";

/**
 * Hook to find libraries that match the user's email domains
 */
export function useMatchingLibraries() {
  const { user, isLoaded } = useUser();

  const verifiedEmails = useMemo(() => {
    if (!user) return [];
    return user.emailAddresses
      .filter((emailAddress) => emailAddress.verification.status === "verified")
      .map((emailAddress) => emailAddress.emailAddress);
  }, [user]);

  const { data: matchingLibraries = [], isLoading } = useQuery({
    queryKey: ["libraries", "matching-by-email", verifiedEmails],
    queryFn: async () => {
      return await getMatchingLibrariesByEmail(verifiedEmails);
    },
    enabled: isLoaded && verifiedEmails.length > 0,
    staleTime: Infinity,
  });

  // Sort libraries alphabetically by library_name
  const sortedMatchingLibraries = useMemo(() => {
    return [...matchingLibraries].sort(
      (a, b) => a?.library_name?.localeCompare(b?.library_name || "") || 0
    );
  }, [matchingLibraries]);

  return {
    matchingLibraries: sortedMatchingLibraries,
    isLoading: !isLoaded || isLoading,
    hasMatches: matchingLibraries.length > 0,
  };
}
